import _ from "lodash";
import React, { useEffect, useState, useRef } from "react";
import { useI18n } from "Hooks";
import withDerivedProps from "lib/withDerivedProps";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

// Utils
import {
  getAIGradingMenuOptions,
  createNonTextToolQueryVariables,
  getPromptBoxQueryVariables,
} from "./modules/AIGradingPromptBoxUtils";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// Constants
import { AI_PROMPTS_ACTION_MAPPING } from "./modules/AIGradingPromptBoxConstants";

// Design system
import { AIPromptBoxV2, Modal, Button } from "@toddle-edu/ds-web";
import { StarsTwoFilled } from "@toddle-edu/ds-icons";

// Modules
import {
  generateAIResponse,
  cancelRequest,
} from "./modules/AIGradingPromptBoxModule";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  setAIGradingProgress,
  createAiAssessmentEvaluation,
} from "ClassRoom/modules/ClassRoomModule";

// Scss
import classes from "./AIGradingPromptBox.scss";

const { TRANSLATE, DEFAULT_LANGUAGE } = AI_PROMPTS_ACTION_MAPPING;

const BLANK_ARRAY = [];

const styles = {
  starsTwoFilledIconStyle: {
    color: "var(--interactive-tertiary)",
  },
};

const getMenuOptionsStyle = optionsCount => {
  // used to show 7th option half visible.
  if (optionsCount === 0) return {};

  return optionsCount > 9 // dividers are also counted as options
    ? { maxHeight: 246 }
    : { maxHeight: optionsCount * 36 };
};

const AIGradingPromptBox = props => {
  const {
    sourceText,
    defaultLanguagePreference,
    promptType,
    onEndAIResponse = _.noop,
    generateAIResponse,
    updatedSourceText,
    curriculumProgramId,
    assessmentToolType,
    assessmentId,
    entityId,
    onStartAIResponse = _.noop,
    inlineSinglePointRubric,
    streamingActionText,
    placeholderText = "",
  } = props;

  // states
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [promptBoxKeywords, setPromptBoxKeywords] = useState("");
  const [
    localDefaultLanguagePreference,
    setLocalDefaultLanguagePreference,
  ] = useState(defaultLanguagePreference);
  const [isPromptDiscardModalOpen, setIsPromptDiscardModalOpen] = useState(
    false
  );

  // hooks
  const { t } = useI18n();
  const promptBoxRef = useRef(null);

  // variables
  const hasUnsubmittedPromptWithoutWarning =
    !_.isEmpty(promptBoxKeywords) && !isPromptDiscardModalOpen;

  // Using the utility functions to create query variables
  const userInputPromptQueryVariables = createNonTextToolQueryVariables({
    entityId,
    curriculumProgramId,
    assessmentId,
    assessmentToolType,
    userInstructions: promptBoxKeywords,
    assessmentToolContext: sourceText,
    languagePreference: localDefaultLanguagePreference,
  });

  const handleSpaceKeyPress = () => {
    const inputElement = promptBoxRef.current;
    if (!inputElement) return;

    const updatedValue = `${inputElement.innerText} `;
    inputElement.innerText = updatedValue;
    setPromptBoxKeywords(updatedValue);

    // Move cursor to the end of the text
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(inputElement);
    range.collapse(false); // false means collapse to end
    selection.removeAllRanges();
    selection.addRange(range);
  };

  // use effects
  useEffect(() => {
    const timer = setTimeout(() => {
      if (promptBoxRef.current) {
        promptBoxRef.current.focus({ preventScroll: true });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const inputElement = promptBoxRef.current;

    const handleKeyDown = event => {
      if (event.key === " " || event.code === "Space") {
        event.preventDefault();
        handleSpaceKeyPress();
      }
    };

    if (inputElement) {
      inputElement.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [promptBoxRef.current]);

  // handle click outside when response is being generated
  useEffect(() => {
    const handleClickOutside = e => {
      if (e.isCustomEvent) return;

      if (isGeneratingResponse || hasUnsubmittedPromptWithoutWarning) {
        e.stopPropagation();
        return;
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [
    isGeneratingResponse,
    promptBoxKeywords,
    hasUnsubmittedPromptWithoutWarning,
  ]);

  // utility functions
  const aiGradingMenuOptions = getAIGradingMenuOptions({
    t,
    promptType,
    defaultLanguagePreference: localDefaultLanguagePreference,
  });

  //close the dropdown by mimicking mousedown event on 'body'
  const customOutsideClickEvent = () => {
    const event = new MouseEvent("mousedown", {
      bubbles: true,
      cancelable: true,
      view: window,
    });

    event.isCustomEvent = true;

    document.body.dispatchEvent(event);
  };

  const onClickOutside = () => {
    if (hasUnsubmittedPromptWithoutWarning) {
      setIsPromptDiscardModalOpen(true);
    }
  };

  const onCloseWarningModal = e => {
    e?.stopPropagation();
    setIsPromptDiscardModalOpen(false);
  };

  const generateTextBasedEvaluation = async ({ input }) => {
    let res;
    try {
      setIsGeneratingResponse(true);

      onStartAIResponse();

      res = await generateAIResponse({
        input,
        updatedSourceText,
      });
    } finally {
      try {
        await onEndAIResponse({ content: res?.content });
      } finally {
        // res is empty when stop button is clicked,
        // don't close the dropdown in that case
        if (res) {
          customOutsideClickEvent();
        }

        // delaying it so that the dropdown is closed before setting the state
        setTimeout(() => {
          setIsGeneratingResponse(false);
        }, 400);
      }
    }
  };

  const onClickStop = () => {
    cancelRequest?.();
    setIsGeneratingResponse(false);
  };

  const handlePromptOptionClick = async option => {
    const { key, parentKey, label } = option;

    // Handle case when user has typed in the prompt box
    if (!_.isEmpty(promptBoxKeywords)) {
      setPromptBoxKeywords("");
      generateTextBasedEvaluation({
        input: userInputPromptQueryVariables,
      });
      return;
    }

    // Handle default language selection
    if (parentKey === DEFAULT_LANGUAGE) {
      setPromptBoxKeywords("");
      const languageId = _.nth(_.split(key, ":"), 1) || "";
      if (!_.isEmpty(languageId)) {
        setLocalDefaultLanguagePreference(
          _.find(AI_TRANSLATION_LANGUAGES, { id: parseInt(languageId) })
        );
      }
      return;
    }

    // Common parameters for all query variable generation
    const commonParams = {
      sourceText,
      inlineSinglePointRubric,
      assessmentToolType,
      assessmentToolContext: sourceText,
      entityId,
      curriculumProgramId,
      assessmentId,
      languagePreference: defaultLanguagePreference,
    };

    let optionForQuery = option;
    if (parentKey === TRANSLATE) {
      optionForQuery = { parentKey, label };
    } else if (!parentKey) {
      optionForQuery = { key };
    }

    const input = getPromptBoxQueryVariables({
      option: optionForQuery,
      ...commonParams,
    });

    if (input) {
      generateTextBasedEvaluation({ input });
    }
  };

  const menuOptions = isGeneratingResponse ? BLANK_ARRAY : aiGradingMenuOptions;

  return (
    <div className={classes.container}>
      <div className={classes.promptBoxWrapper}>
        <AIPromptBoxV2
          ref={promptBoxRef}
          menuOptions={menuOptions}
          onChange={setPromptBoxKeywords}
          onClickOutside={onClickOutside}
          onSubmit={handlePromptOptionClick}
          streamingActionText={streamingActionText}
          isStreaming={isGeneratingResponse}
          stopStreamingButtonText={t("common:stop_esc")}
          onStop={onClickStop}
          onOptionClick={handlePromptOptionClick}
          placeholder={placeholderText || t("ai:prompt_box_placeholder_text")}
          menuStyle={getMenuOptionsStyle(_.size(menuOptions))}
          shouldStopPropagation={true}
          OverrideIconComponent={
            <div className={classes.aiIcon}>
              <StarsTwoFilled style={styles.starsTwoFilledIconStyle} />
            </div>
          }
        />
      </div>
      {isPromptDiscardModalOpen ? (
        <Modal
          width="440px"
          height="fit-content"
          isOpen={isPromptDiscardModalOpen}
          onClose={onCloseWarningModal}
          dsVersion="2.0"
        >
          <Modal.Header isClosable title={t("journal:discard_prompt")} />
          <Modal.Body>{t("journal:discard_prompt_message")}</Modal.Body>
          <Modal.Footer>
            <div className={classes.footerContainer}>
              <Button
                dsVersion="2.0"
                size="medium"
                variant="neutral"
                type="outlined"
                onClick={onCloseWarningModal}
              >
                {t("journal:continue_writing")}
              </Button>
              <Button
                dsVersion="2.0"
                size="medium"
                variant="destructive"
                onClick={customOutsideClickEvent}
              >
                {t("common:discard_and_exit")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    ""
  );

  return {
    curriculumProgramId,
  };
};

const getDerivedProps = () => {
  const defaultLanguagePreferenceId = getSettingValue({
    name: "ConfigureAIGradingVoiceLanguage",
    readFromUserAndOrgLevel: true,
  });

  const defaultLanguagePreference =
    _.find(AI_TRANSLATION_LANGUAGES, {
      id: parseInt(defaultLanguagePreferenceId),
    }) || _.find(AI_TRANSLATION_LANGUAGES, { code: "en" });

  return {
    defaultLanguagePreference,
  };
};

const mapActionCreators = {
  generateAIResponse,
  updateSettings,
  setAIGradingProgress,
  createAiAssessmentEvaluation,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps)
)(AIGradingPromptBox);
